import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { red } from '@mui/material/colors';
import { useAppDispatch } from '../../hooks/redux';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import useGetMetadataQuery from '../../reactQuery/hooks/useGetMetadata';
import InlineField from '../FormFields/InlineField/InlineField';
import Loader from '../Simple/Loader/Loader';
import Form from '../Form/Form';
import { IUserProfileFields, User } from '../../lib/interfaces/User';
import { updateUserProfile, changePassword } from '../../api/apiRequests/user';
import UserAvatar from './UserAvatar';
import paths from '../../lib/consts/paths';
import { changePasswordFields, SUBMIT_BTN_TYPE } from '../Form/form.utils';
import { FormField } from '../../lib/interfaces/Form';
// import { contentMandatoryFields } from '../Form/form.utils';
import './UserProfilePage.scss';
import {
	CONTENT_FIELDS,
	getContentFieldValue,
} from '../../lib/helpers/content.helper';
import { getUserProfileFields } from './userProfileFormFields';
import reactQueryKeys from '../../reactQuery/reactQueryKeys';
import { setForgotPasswordModalOpen } from '../../redux/reducers/ui/modalsReducer';
import TrainingApisToken from './TrainingApisToken';

const UserProfilePage: FC = () => {
	const { t, i18n } = useTranslation();
	const { data: currentUser } = useUserMeQuery();
	const { data: metadata } = useGetMetadataQuery();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const currentLanguage: string = i18n.language;
	const queryClient = useQueryClient();
	const [showNewPasswordError, setShowNewPasswordError] =
		useState<boolean>(false);
	const [showNewPasswordSuccess, setShowNewPasswordSuccess] =
		useState<boolean>(false);

	const userMutation = useMutation<
		Partial<IUserProfileFields>,
		AxiosError<any>,
		Partial<IUserProfileFields>
	>((data) => updateUserProfile(data), {
		onSuccess: (response) => {
			const previousUser: User | undefined = queryClient.getQueryData(
				reactQueryKeys.userMe,
			);
			if (previousUser && response) {
				queryClient.setQueryData(reactQueryKeys.userMe, {
					...previousUser,
					...response,
				});
			}
		},
	});

	const onSave = (value: string, fieldName: keyof IUserProfileFields) => {
		if (currentUser?.[fieldName] !== value) {
			const data = { [fieldName]: value } as Partial<IUserProfileFields>;
			userMutation.mutate(data);
		}
	};

	const userProfileFields = getUserProfileFields(t);

	const profileUser = currentUser ? (currentUser as IUserProfileFields) : null;

	const subscriptionPlanName = (): string | null => {
		if (currentUser && metadata) {
			const { subscriptionPlanId } = currentUser;
			const userSubscriptionPlan = metadata.subscriptionPlans?.find(
				(field) => field.id === subscriptionPlanId,
			);
			return getContentFieldValue({
				entityContent: userSubscriptionPlan?.content,
				field: CONTENT_FIELDS.NAME,
				language: currentLanguage,
			});
		}
		return null;
	};

	const onUserAvatarUpdate = (data: Partial<IUserProfileFields>) => {
		userMutation.mutate(data);
	};

	const changePasswordFormFields = changePasswordFields.map(
		(field: FormField) => {
			if (field.label) {
				field.label = t(field.label);
			}
			return field;
		},
	);

	const changePasswordMutation = useMutation<any, AxiosError<any>, string>(
		(data: any) => changePassword(data),
		{
			onSuccess() {
				setShowNewPasswordSuccess(true);
				setShowNewPasswordError(false);
			},
			onError: (error) => {
				if (error?.response?.status === 401) {
					setShowNewPasswordError(true);
					setShowNewPasswordSuccess(false);
				}
			},
		},
	);

	const handleSubmit = (formValuesCP: any) => {
		changePasswordMutation.mutate(formValuesCP);
	};

	const handleForgotPasswordClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(setForgotPasswordModalOpen(true));
	};

	return (
		<>
			{profileUser && (
				<div className="UserProfilePage">
					<Typography
						component="h1"
						variant="h5"
						sx={{ marginBottom: '10px', fontWeight: 500 }}
					>
						{t('hello')}, {currentUser?.firstName}!
					</Typography>
					<div className="UserProfilePage__cols">
						<div className="UserProfilePage__col-wrapper">
							<div className="UserProfilePage__col">
								<UserAvatar onUserAvatarUpdate={onUserAvatarUpdate} />
							</div>
							<div className="UserProfilePage__col">
								{userProfileFields.map((field) => (
									<div key={field.name} style={{ paddingBottom: '20px' }}>
										<Typography
											component="div"
											variant="body1"
											sx={{ fontWeight: 600, paddingBottom: '10px' }}
										>
											{field.label}
										</Typography>
										{field.name !== 'avatar' ? (
											<InlineField
												disabled={field.disabled}
												contentValue={profileUser[field.name]}
												validation={field.validation}
												emptyValue={t('emptyInlineTextValue')}
												onSave={(value) => {
													onSave(value, field.name);
												}}
											/>
										) : null}
									</div>
								))}
							</div>
						</div>
						<div className="UserProfilePage__col">
							<div>
								<Typography
									component="div"
									variant="body1"
									sx={{ fontWeight: 600, paddingBottom: '10px' }}
								>
									{t('subscriptionPlan')}
								</Typography>
								<div className="UserProfilePage__subsctription-wrapper">
									<Typography
										component="div"
										variant="h6"
										sx={{
											fontWeight: 600,
										}}
									>
										{subscriptionPlanName()}
									</Typography>
									<Button
										color="primary"
										variant="contained"
										onClick={() => navigate(paths.plans)}
										className="text-elem"
										size="small"
										disableElevation
										sx={{
											marginLeft: '10px',
										}}
									>
										{t('seeAllPlans')}
									</Button>
								</div>
							</div>
							<div>
								<TrainingApisToken />
							</div>
						</div>
					</div>
					<div className="UserProfilePage__form-wrapper">
						<Typography
							component="div"
							variant="body1"
							sx={{ fontWeight: 600, paddingBottom: '10px' }}
						>
							{t('Change password')}
						</Typography>
						<Form
							fields={changePasswordFormFields}
							onSubmit={handleSubmit}
							saveButtonName={t('send')}
							saveButtonStyle={SUBMIT_BTN_TYPE.FULL}
							fullWidth
						/>
						{showNewPasswordError && (
							<Typography component="p" variant="body1" color={red[500]}>
								{t('oldPasswordDoesntMatch')}
							</Typography>
						)}
						{showNewPasswordSuccess && (
							<Typography component="p" variant="body1">
								{t('changePasswordSuccess')}
							</Typography>
						)}

						<a href="forgotPassword" onClick={(e) => handleForgotPasswordClick(e)}>
							{t('forgotPassword')}
						</a>
					</div>
				</div>
			)}
			{!currentUser && <Loader />}
		</>
	);
};

export default UserProfilePage;
